import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

let deferredPrompt = null;

const InstallPWA = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);
  const { t } = useTranslation();
  const canInstall = Boolean(deferredPrompt) && !manualOnly;

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setManualOnly(false);
      const dismissed = localStorage.getItem('veonfox_pwa_dismissed');
      if (!dismissed) setShowPrompt(true);
    };
    const requestInstall = () => { setManualOnly(!deferredPrompt); setShowPrompt(true); };
    const installed = () => { deferredPrompt = null; setShowPrompt(false); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('veonfox:install', requestInstall);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('veonfox:install', requestInstall);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setShowPrompt(false);
    } catch {
      setManualOnly(true);
    } finally {
      deferredPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('veonfox_pwa_dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 z-50 animate-in slide-in-from-bottom-4" data-testid="pwa-install-prompt">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 p-4 flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0f172a] to-[#1e293b] flex items-center justify-center flex-shrink-0">
          <img src="/icon-96.png" alt="VeonFox" className="w-8 h-8 rounded-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('pwa.install')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{canInstall ? t('pwa.description') : t('pwa.manualInstall')}</p>
          <div className="flex gap-2 mt-2.5">
            {canInstall && <button type="button" onClick={handleInstall} data-testid="pwa-install-button"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#006fba] text-white text-xs font-medium hover:bg-[#005f9f] transition-colors">
              <Download className="w-3.5 h-3.5" /> {t('pwa.installButton')}
            </button>}
            <button onClick={handleDismiss} data-testid="pwa-dismiss-button"
              className="px-3 py-1.5 rounded-lg text-gray-400 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              {t('pwa.later')}
            </button>
          </div>
        </div>
        <button type="button" onClick={handleDismiss} aria-label={t('common.cancel')} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;
